'use client';

import { ENGINE_PATH_PREFIX, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import * as ENGINE from '@gnsx/genesys.js';
import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { ComboboxGroup } from '@/components/ui/combobox';
import type { IAssetFile } from '@/editor/store/models/assetTree';
import type { Instance } from 'mobx-state-tree';

import { AssetType } from '@/editor/core/assetTypes';
import { isSubclass } from '@/editor/core/classHelpers';
import { assetsStore, editorStore } from '@/editor/store';

/**
 * Property model for `Spawnable<T>` fields.
 * The value is a string that is either a registered class name (e.g. `'ENGINE.ModelMeshActor'`)
 * or a prefab asset path (e.g. `'@project/prefabs/MyEnemy.prefab.json'`).
 * Options are split into two groups: matching actor classes and matching prefabs.
 */
export const SpawnableProperty = t.compose('SpawnableProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Spawnable),
    value: t.optional(t.string, ''),
    defaultValue: t.optional(t.string, ''),
    /** Short actor class name used to filter classes and prefabs (e.g. 'ModelMeshActor') */
    spawnableClass: t.maybe(t.string),
  })
).views(self => ({
  /** All actor class options that are subclasses of spawnableClass */
  get classOptions(): Array<{ label: string; value: string }> {
    const registry = ENGINE.ClassRegistry.getRegistry();
    const baseConstructor = resolveBaseConstructor(self.spawnableClass);
    const options: Array<{ label: string; value: string }> = [];

    options.push({ label: 'None', value: '' });

    for (const [className, constructor] of registry.entries()) {
      if (baseConstructor && !isSubclass(constructor, baseConstructor)) {
        continue;
      }
      const shortName = className.split('.').pop() ?? className;
      options.push({ label: shortName, value: className });
    }

    return options;
  },
  /** All prefab options filtered by spawnableClass (optimistic when class not yet loaded) */
  get prefabOptions(): Array<{ label: string; value: string }> {
    const files = assetsStore.getAssetSelectFiles(
      AssetType.Prefab,
      undefined,
      self.spawnableClass ?? undefined
    );
    return files.map(file => ({
      label: getPrefabLabel(file.path),
      value: file.path,
      asset: file,
    }));
  },
  /** Combined combobox groups: Prefabs first, then Classes */
  get groups(): ComboboxGroup[] {
    const showEngineAssets = editorStore.inspectorPanel.showEngineAssets;

    const prefabFiles = assetsStore.getAssetSelectFiles(
      AssetType.Prefab,
      undefined,
      self.spawnableClass ?? undefined
    );
    const filteredPrefabFiles = showEngineAssets
      ? prefabFiles
      : prefabFiles.filter(file => !file.path.startsWith(ENGINE_PATH_PREFIX));

    const prefabGroups = groupPrefabsByPath(filteredPrefabFiles);

    const classGroup: ComboboxGroup = {
      label: 'Classes',
      options: this.classOptions,
    };

    return [...prefabGroups, classGroup];
  },
  get displayValue(): string {
    if (!self.value) return '';
    // Prefab path
    if (self.value.includes('/')) {
      return getPrefabLabel(self.value);
    }
    // Class name: strip prefix
    return self.value.split('.').pop() ?? self.value;
  },
})).actions(self => ({
  setValue(value: string, fromPanel = true, initial = false) {
    if (value === self.value) return;
    self.value = value;
    self.notifyValueChange(self.path, value, { fromPanel, initial, property: self as Instance<typeof SpawnableProperty> });
  },
  setDefaultValue(value: string) {
    self.defaultValue = value;
  },
}));

export interface ISpawnableProperty extends Instance<typeof SpawnableProperty> { }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strips the prefab extension and returns just the file name. */
function getPrefabLabel(prefabPath: string): string {
  const name = prefabPath.split('/').pop() ?? prefabPath;
  return name.replace(ENGINE.PREFAB_EXTENSION, '');
}

/**
 * Resolves the base constructor from the ClassRegistry given a short or qualified class name.
 * Returns null if the class cannot be found.
 */
function resolveBaseConstructor(spawnableClass: string | undefined | null): (new (...args: any[]) => any) | null {
  if (!spawnableClass) return null;

  // Use the null-safe lookup which searches by short constructor name across all prefixes
  return ENGINE.ClassRegistry.getClassByConstructorName(spawnableClass) ?? null;
}

function getPrefabGroupLabel(prefabPath: string): string {
  const parts = prefabPath.split('/');
  if (parts.length <= 1) return 'Prefabs';
  return parts.slice(0, -1).join('/');
}

function groupPrefabsByPath(files: IAssetFile[]): ComboboxGroup[] {
  const groups = new Map<string, Array<{ label: string; value: string }>>();

  for (const file of files) {
    const p = file.path;
    const groupLabel = getPrefabGroupLabel(p);
    const option = { label: getPrefabLabel(p), value: p, asset: file };
    if (!groups.has(groupLabel)) {
      groups.set(groupLabel, []);
    }
    groups.get(groupLabel)!.push(option);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      if (a.startsWith(PROJECT_PATH_PREFIX)) return -1;
      if (b.startsWith(PROJECT_PATH_PREFIX)) return 1;
      if (a.startsWith(ENGINE_PATH_PREFIX)) return 1;
      if (b.startsWith(ENGINE_PATH_PREFIX)) return -1;
      return a.localeCompare(b);
    })
    .map(([label, options]) => ({
      label,
      options: options.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}
